import { useEffect, useState, useCallback } from "react";
import {
  Users, UserCheck, Music, Activity, AlertTriangle, HardDrive, Cpu, Crown,
  RefreshCw, Ban, CheckCircle2, ShieldCheck, Server,
} from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [health, setHealth] = useState(null);

  const loadAll = useCallback(async () => {
    const [s, u, j, h] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/users"),
      api.get("/admin/jobs"),
      api.get("/admin/health"),
    ]);
    setStats(s.data); setUsers(u.data); setJobs(j.data); setHealth(h.data);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const suspend = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}/suspend`, { suspended: !u.suspended });
      loadAll();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const retry = async (j) => {
    await api.post(`/admin/jobs/${j.id}/retry`);
    toast.success("Job re-queued");
    setTimeout(loadAll, 1500);
  };

  const cards = stats ? [
    { icon: Users, label: "Total Users", value: stats.total_users, color: "#06B6D4" },
    { icon: UserCheck, label: "Active (7d)", value: stats.active_users, color: "#10B981" },
    { icon: Music, label: "Songs Analyzed", value: stats.songs_analyzed, color: "#F59E0B" },
    { icon: Activity, label: "Processing Jobs", value: stats.processing_jobs, color: "#8B5CF6" },
    { icon: AlertTriangle, label: "Failed Jobs", value: stats.failed_jobs, color: "#EF4444" },
    { icon: HardDrive, label: "Storage (MB)", value: stats.storage_mb, color: "#F43F5E" },
    { icon: Cpu, label: "API Calls", value: stats.api_usage, color: "#06B6D4" },
    { icon: Ban, label: "Suspended", value: stats.suspended_users, color: "#EF4444" },
  ] : [];

  return (
    <div className="space-y-6 sw-fade-up">
      <header className="flex items-center gap-2">
        <ShieldCheck className="text-cyan-400" size={24} />
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-white">Admin</h1>
      </header>

      <div className="flex gap-2 overflow-x-auto">
        {["overview", "users", "jobs", "health"].map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors ${
              tab === t ? "bg-cyan-500 text-[#0B0E14]" : "bg-[#131822] text-slate-400 hover:text-white"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {cards.map((c) => (
              <div key={c.label} data-testid={`stat-${c.label}`} className="rounded-xl border border-[#222A38] bg-[#131822] p-4">
                <c.icon size={18} style={{ color: c.color }} />
                <div className="mt-2 font-display text-2xl font-extrabold text-white">{c.value}</div>
                <div className="font-mono-sw text-[10px] uppercase tracking-wider text-slate-500">{c.label}</div>
              </div>
            ))}
          </div>
          {stats && (
            <div className="rounded-xl border border-[#222A38] bg-[#131822] p-5">
              <div className="mb-3 flex items-center gap-2 font-display font-bold text-white"><Crown size={16} className="text-amber-400" /> Subscriptions</div>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(stats.subscriptions).map(([plan, count]) => (
                  <div key={plan} className="rounded-lg bg-[#0B0E14] p-3 text-center">
                    <div className="font-display text-xl font-extrabold text-white">{count}</div>
                    <div className="font-mono-sw text-[10px] uppercase text-slate-500">{plan}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {tab === "users" && (
        <div className="overflow-x-auto rounded-xl border border-[#222A38] bg-[#131822]">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-[#222A38] font-mono-sw text-[11px] uppercase text-slate-500">
              <tr>
                <th className="p-3">User</th><th className="p-3">Plan</th><th className="p-3">Songs</th><th className="p-3">Status</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-b border-[#1A202C]">
                  <td className="p-3">
                    <div className="font-semibold text-white">{u.name} {u.role === "admin" && <span className="text-cyan-400">★</span>}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </td>
                  <td className="p-3"><span className="font-mono-sw text-xs uppercase text-slate-400">{u.plan}</span></td>
                  <td className="p-3 text-slate-300">{u.songs}</td>
                  <td className="p-3">
                    {u.suspended ? <span className="text-red-400">Suspended</span> : <span className="text-emerald-400">Active</span>}
                  </td>
                  <td className="p-3">
                    {u.role !== "admin" && (
                      <button
                        data-testid={`toggle-suspend-${u.id}`}
                        onClick={() => suspend(u)}
                        className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${u.suspended ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}
                      >
                        {u.suspended ? "Reinstate" : "Suspend"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "jobs" && (
        <div className="grid gap-2">
          {jobs.length === 0 && <div className="rounded-xl border border-dashed border-[#222A38] p-8 text-center text-sm text-slate-500">No processing jobs.</div>}
          {jobs.map((j) => (
            <div key={j.id} data-testid={`admin-job-${j.id}`} className="flex items-center gap-3 rounded-xl border border-[#222A38] bg-[#131822] p-3.5">
              <span className={`h-2.5 w-2.5 rounded-full ${
                j.status === "completed" ? "bg-emerald-400" : j.status === "failed" ? "bg-red-400" : "bg-cyan-400 animate-pulse"
              }`} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold text-white">{j.song_title}</div>
                <div className="font-mono-sw text-[11px] text-slate-500">{j.status} · {j.stages?.length} stages</div>
                {j.error && <div className="font-mono-sw text-[11px] text-red-400">{j.error}</div>}
              </div>
              {j.status === "failed" && (
                <button data-testid={`admin-retry-${j.id}`} onClick={() => retry(j)} className="flex items-center gap-1 rounded-lg bg-[#1A202C] px-3 py-1.5 text-xs font-semibold text-cyan-400">
                  <RefreshCw size={13} /> Retry
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "health" && health && (
        <div className="grid gap-3 sm:grid-cols-2">
          {Object.entries(health).map(([k, v]) => {
            const ok = v === "healthy";
            return (
              <div key={k} data-testid={`health-${k}`} className="flex items-center gap-3 rounded-xl border border-[#222A38] bg-[#131822] p-4">
                {ok ? <CheckCircle2 className="text-emerald-400" size={20} /> : <Server className="text-amber-400" size={20} />}
                <div>
                  <div className="font-semibold capitalize text-white">{k.replace(/_/g, " ")}</div>
                  <div className="font-mono-sw text-xs text-slate-400">{v}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
